import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift, faCopy, faCheck, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './InviteFriends.css';

const API = process.env.REACT_APP_API_BASE_URL;

const STATUS = {
  booked: { label: 'Booked a free class', tone: 'is-info' },
  trial: { label: 'Free class planned', tone: 'is-info' },
  joined: { label: 'Joined 🎉', tone: 'is-good' },
  paid: { label: 'Discount earned', tone: 'is-gold' },
  rewarded: { label: 'Discount used', tone: 'is-off' },
};

const message = (name, link, step) =>
  `Hi! ${name || 'My child'} is learning coding with RoboMind and loves it 🤖\nBook a free class for your child here, and get ${step}% off the first package if you join:\n${link}`;

/* "Invite a friend" card on the student dashboard (referral program).
   Hidden for anyone who can't refer (e.g. demo students). */
const InviteFriends = () => {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/api/student/referral`, { headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } })
      .then((res) => !cancelled && setData(res.data))
      .catch(() => {}); // not available: show nothing
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  if (!data?.code) return null;

  const link = `${window.location.origin}/FreeTrial?ref=${data.code}`;
  const text = message(data.firstName, link, data.step);
  const joinedCount = data.friends.filter((f) => ['joined', 'paid', 'rewarded'].includes(f.status)).length;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <section className="rmsi" aria-labelledby="rmsi-title">
      <div className="rmsi-top">
        <span className="rmsi-icon" aria-hidden="true">
          <FontAwesomeIcon icon={faGift} />
        </span>
        <div>
          <h2 id="rmsi-title">Invite a friend, you both save</h2>
          <p>
            Your friend gets <b>{data.step}% off</b> their first package. You get <b>{data.step}% off</b> your next package for every friend who joins, up to{' '}
            {data.cap}%.
          </p>
        </div>
      </div>

      {data.discount.percent > 0 && (
        <p className="rmsi-reward">
          <FontAwesomeIcon icon={faGift} /> <b>{data.discount.percent}% off</b> your next package is waiting for you!
          {data.discount.waiting > 0 && ` (+${data.discount.waiting * data.step}% for the package after)`}
        </p>
      )}

      <div className="rmsi-share">
        <div className="rmsi-link">
          <span>Your link</span>
          <code>{link.replace(/^https?:\/\//, '')}</code>
        </div>
        <button type="button" className="rmsi-btn rmsi-btn--ghost" onClick={copy}>
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} /> {copied ? 'Copied' : 'Copy link'}
        </button>
        <a className="rmsi-btn rmsi-btn--wa" href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer">
          <FontAwesomeIcon icon={faWhatsapp} /> Share on WhatsApp
        </a>
      </div>

      <div className="rmsi-friends">
        <h3>
          <FontAwesomeIcon icon={faUserPlus} /> Friends you invited {data.friends.length > 0 && <span>{joinedCount} joined</span>}
        </h3>
        {data.friends.length === 0 ? (
          <p className="rmsi-empty">No friends yet. Share your link to invite the first one!</p>
        ) : (
          <ul>
            {data.friends.map((f) => {
              const st = STATUS[f.status] || STATUS.booked;
              return (
                <li key={f.id} className={st.tone}>
                  <b>{f.name}</b>
                  <span>{st.label}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
};

export default InviteFriends;
